import WorkspacesIcon from "@mui/icons-material/Workspaces";
import { DialogActions, DialogContent } from "@mui/material";
import {
  Button,
  CreatedUpdatedBy,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  TextField,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import TableEditor from "components/TableEditor/TableEditor";
import HistoryListDialog from "features/histories/HistoryListDialog";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { createContext } from "react";
import useSeatingLayoutForm, {
  UseSeatingLayoutFormProps,
  UseSeatingLayoutFormReturnType,
} from "./useSeatingLayoutForm";
export const SeatingLayoutContext = createContext<
  UseSeatingLayoutFormReturnType | undefined
>(undefined);
export interface SeatingLayoutFormProps extends UseSeatingLayoutFormProps {}

export default function SeatingLayoutForm({
  id,
  afterSave,
  onClose,
  defaultValues,
}: SeatingLayoutFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const form = useSeatingLayoutForm({
    id,
    afterSave,
    defaultValues,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
  } = form;
  const title = isCreate ? I18n.t("SeatingLayout.addTitle") : item.name;
  const subtitle = isCreate ? undefined : undefined;
  return (
    <ParamsContext.Provider value={{ partner_group_id: id }}>
      <SeatingLayoutContext.Provider value={form}>
        <PageHeader
          avatar={<WorkspacesIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="SeatingLayout.attributes"
            translationPlaceholdersCategory="SeatingLayout.placeholders"
            translationHelpersCategory="SeatingLayout.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("partner_group", item)}
          >
            <FieldsContainer>
              <Field fullWidth>
                <TextField id="name" />
              </Field>
              <Field fullWidth>
                <TableEditor id="layout" />
              </Field>
            </FieldsContainer>
          </Form>
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="SeatingLayout"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          <Button
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
          />
        </DialogActions>
      </SeatingLayoutContext.Provider>
    </ParamsContext.Provider>
  );
}
